/**
 * FreeToolsGrid — 8 free landlord-facing tools/utilities surfaced on the
 * public homepage. Every tile links to an EXISTING working page — no
 * dysfunctional stubs. Placed above the newsletter/footer so mobile
 * visitors see high-utility CTAs after the hero.
 */
import { Link } from "react-router-dom";
import {
  Wrench, LineChart, TrendingUp, MapPin, DoorOpen,
  Layout, Sparkles, Video, ArrowRight, FileWarning,
} from "lucide-react";

const TOOLS = [
  {
    icon: FileTextIcon,
    title: "Rent-Increase Notice",
    tagline: "Correct provincial form + notice date in 10s.",
    href: "/tools/rent-increase-notice",
    accent: "emerald",
    testid: "free-tool-rent-increase",
  },
  {
    icon: FileWarning,
    title: "N4 / Late-Rent Notice",
    tagline: "Provincial arrears notice + cure period in 10s.",
    href: "/tools/n4-late-rent-notice",
    accent: "rose",
    testid: "free-tool-n4-late-rent",
  },
  {
    icon: Wrench,
    title: "Maintenance Cost Predictor",
    tagline: "Ballpark repair costs by category & province.",
    href: "/tools/roi-calculator",
    accent: "sky",
    testid: "free-tool-maintenance-cost",
  },
  {
    icon: LineChart,
    title: "Live Market Intelligence",
    tagline: "Real-time CAD rent + mortgage benchmarks.",
    href: "/mortgage-rates",
    accent: "indigo",
    testid: "free-tool-market-intel",
  },
  {
    icon: TrendingUp,
    title: "Local Rent Trend Tracker",
    tagline: "Track median rent by city & unit type.",
    href: "/tools/rent-affordability",
    accent: "rose",
    testid: "free-tool-rent-trends",
  },
  {
    icon: MapPin,
    title: "Neighborhood Scorecards",
    tagline: "Vacancy, income & tenant-quality signals.",
    href: "/landlord-software/city/toronto",
    accent: "amber",
    testid: "free-tool-neighborhood-scorecard",
  },
  {
    icon: DoorOpen,
    title: "Recent Vacancy Rates",
    tagline: "CMHC-sourced vacancy % by city.",
    href: "/landlord-software/city/vancouver",
    accent: "teal",
    testid: "free-tool-vacancy-rates",
  },
  {
    icon: Layout,
    title: "Frictionless Portals",
    tagline: "Preview the tenant + landlord portals free.",
    href: "/for/landlords",
    accent: "slate",
    testid: "free-tool-portals",
  },
  {
    icon: Sparkles,
    title: "Instant Tenant Matcher",
    tagline: "Get matched with pre-qualified renters.",
    href: "/find-tenants",
    accent: "emerald",
    testid: "free-tool-tenant-matcher",
  },
  {
    icon: Video,
    title: "3D Virtual Tour Gallery",
    tagline: "Browse Matterport-style rental walkthroughs.",
    href: "/features/virtual-staging",
    accent: "violet",
    testid: "free-tool-3d-tour",
  },
];

function FileTextIcon(props) {
  // Local inline icon so we don't pull one more lucide import.
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z"/>
      <path d="M14 2v6h6M9 13h6M9 17h4"/>
    </svg>
  );
}

const ACCENTS = {
  emerald: { icon: "text-emerald-700 bg-emerald-50 border-emerald-200", hover: "hover:border-emerald-400" },
  sky:     { icon: "text-sky-700 bg-sky-50 border-sky-200",           hover: "hover:border-sky-400" },
  indigo:  { icon: "text-indigo-700 bg-indigo-50 border-indigo-200",  hover: "hover:border-indigo-400" },
  rose:    { icon: "text-rose-700 bg-rose-50 border-rose-200",        hover: "hover:border-rose-400" },
  amber:   { icon: "text-amber-700 bg-amber-50 border-amber-200",     hover: "hover:border-amber-400" },
  teal:    { icon: "text-teal-700 bg-teal-50 border-teal-200",        hover: "hover:border-teal-400" },
  slate:   { icon: "text-slate-700 bg-slate-100 border-slate-200",    hover: "hover:border-slate-400" },
  violet:  { icon: "text-violet-700 bg-violet-50 border-violet-200",  hover: "hover:border-violet-400" },
};

export default function FreeToolsGrid() {
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16" data-testid="free-tools-section">
      <div className="text-[11px] uppercase tracking-[0.22em] font-black text-emerald-700 mb-2">
        Free landlord tools · No signup required
      </div>
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-black tracking-tight text-slate-900">
          Instant tools that solve one thing well.
        </h2>
        <div className="text-sm text-slate-600 max-w-md">
          Built for Canadian landlords. Every tool below is live, free forever, and works without an account.
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="free-tools-grid">
        {TOOLS.map(t => {
          const a = ACCENTS[t.accent] || ACCENTS.emerald;
          return (
            <Link
              key={t.testid}
              to={t.href}
              data-testid={t.testid}
              className={`group relative rounded-2xl bg-white border border-slate-200 ${a.hover} p-5 transition-all shadow-sm hover:shadow-md`}
            >
              <div className="font-display text-base font-black text-slate-900">{t.title}</div>
              <p className="mt-1 text-sm text-slate-600 leading-relaxed">{t.tagline}</p>
              <div className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-emerald-700 group-hover:gap-2 transition-all">
                Open tool <ArrowRight className="w-3.5 h-3.5"/>
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
}
